<template>
  <div class="w-full">
    <div class="flex items-center justify-between">
      <h3 class="py-0 my-0 text-xl text-gray-800">
        Ersatzteilanfragen
      </h3>

      <mia-submit-button
        type="button"
        label="Neuer Eintrag"
        @click="$router.push({ name: 'ersatzteilanfrage.create' })"
      />
    </div>

    <mia-wait for="loading filters">
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <template #default>
        <div class="grid grid-cols-3 gap-6 p-4 mt-6 -mx-2 bg-gray-200">
          <mia-feature-select
            v-model="filters.mechanic"
            label="Mitarbeiter"
            :options="mechanics"
            :name-by="(mechanic) => `${mechanic.item_firstname} ${mechanic.item_lastname}`"
            track-by="id"
            @input="loadSparePartRequests"
          />

          <mia-feature-input
            v-model="filters.orderId"
            label="Vorgangsnummer"
            @blur="loadSparePartRequests"
          />

          <div class="flex items-end justify-end">
            <mia-cancel-button
              label="Zurücksetzen"
              @click="resetFilters"
            />
          </div>
        </div>

        <div class="mt-6">
          <mia-wait for="loading spare part requests">
            <template #waiting>
              <div class="flex items-center justify-center py-8">
                <mia-wave-loader />
              </div>
            </template>

            <template #default>
              <datatable
                :columns="columns"
                :data="sortedSparePartRequests"
                class="sticky w-full table-auto datatable"
              >
                <template #no-results>
                  <div class="text-center">
                    Für die gewählten Filter wurden keine Einträge gefunden.
                  </div>
                </template>
              </datatable>
            </template>
          </mia-wait>
        </div>
      </template>
    </mia-wait>

    <mia-confirmation-modal
      v-if="deletingSparePartRequest"
      id="delete-blog-entry-confirmation-modal"
      :show="deletingSparePartRequest !== null"
      title="Eintrag löschen"
      confirm-classes="border-red-500 bg-red-500 text-white hover:bg-red-600 hover:border-red-600"
      confirm-text="Eintrag löschen"
      cancel-text="Abbrechen"
      width="40%"
      :is-loading="$wait.is('deleting spare part request')"
      @closed="deletingSparePartRequest = null"
      @cancel="deletingSparePartRequest = null"
      @confirm="onDeleteSparePartRequestConfirmed"
    >
      <div class="p-4">
        Sind Sie sicher, dass Sie den Eintrag <b>{{ deletingSparePartRequest.item_title }}</b> endgültig löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
      </div>
    </mia-confirmation-modal>

    <mia-modal
      v-if="showCommentModal"
      id="task-comment-modal"
      :click-to-close="false"
      @closed="closeCommentModal()"
    >
      <mia-modal-header>
        Informationen
      </mia-modal-header>

      <div class="p-4 flex flex-col">
        <template>
          <div
            v-if="selectedTask"
            class="flex flex-col p-1"
          >
            <div class="flex flex-col">
              <div class="flex">
                <div
                  v-if="selectedTask.item_assignee"
                  class="relative"
                >
                  <div
                    class="flex items-center justify-center w-10 h-10 overflow-hidden bg-gray-400 rounded-full ring-8 ring-white"
                  >
                    <img
                      class="w-full"
                      :src="selectedTask.item_assignee.item_image"
                      alt=""
                    >
                  </div>
                </div>
                <div v-else>
                  <div
                    class="relative flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full ring-8 ring-white"
                  >
                    <!-- Heroicon name: solid/user-circle -->
                    <svg
                      class="w-5 h-5 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div class="flex ml-2">
                  <span
                    v-if="selectedTask.item_assignee"
                    class="py-2"
                  >{{ selectedTask.item_assignee.item_firstname }} {{ selectedTask.item_assignee.item_lastname }}</span>
                  <span
                    v-else
                    class="py-2"
                  >Aufgabe noch nicht zugewiesen.</span>
                </div>
              </div>
              <div class="flex mt-4">
                <span class="text-lg">{{ selectedTask.item_title }}</span>
              </div>
              <div class="flex mt-1 justify-between">
                <span class="text-xs text-gray-600">{{ selectedTask.item_created_at | datetime }}</span>
                <span
                  v-if="selectedTask.item_completed === '1'"
                  class="text-xs text-green-400"
                >Erledigt am: {{ selectedTask.item_completed_at | datetime }}</span>
              </div>
              <div class="flex mt-4">
                <p>{{ selectedTask.item_description }}</p>
              </div>
            </div>
            <hr>
            <template v-if="selectedTask.item_comments.length > 0">
              <div
                v-for="comment in selectedTask.item_comments"
                :key="comment.id"
                class="flex flex-col bg-gray-200 p-4 mb-4"
              >
                <div class="flex mb-2">
                  <span>{{ comment.item_firstname }} {{ comment.item_lastname }}</span>
                  <span class="text-xs ml-2">{{ comment.item_created_at | datetime }}</span>
                </div>
                <p>{{ comment.item_comment }}</p>
              </div>
            </template>
            <div
              v-else
              class="flex justify-center"
            >
              <p>Keine Kommentare vorhanden.</p>
            </div>
          </div>
          <div
            class="text-sm"
          >
            <label class="mb-1 mr-2 tracking-wider text-gray-800">
              Kommentar schreiben
            </label>
            <mia-feature-textarea v-model="selectedTask.new_comment" />
          </div>
        </template>
      </div>

      <mia-modal-footer>
        <div class="flex justify-between w-full">
          <div class="space-x-4">
            <mia-button
              class="px-4 py-2 text-white transition-colors duration-150 bg-green-600 hover:bg-green-700 ease-out-fast"
              type="button"
              @click="onSaveComment"
            >
              Speichern
            </mia-button>
          </div>

          <div class="flex justify-end">
            <mia-button
              class="px-4 py-2 transition-colors duration-150 hover:bg-gray-400 ease-out-fast"
              type="button"
              @click="closeCommentModal()"
            >
              Abbrechen
            </mia-button>
          </div>
        </div>
      </mia-modal-footer>
    </mia-modal>
  </div>
</template>

<script>
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureTextarea from '@/components/cars/edit/FeatureTextarea.vue'
import { DateTime } from 'luxon'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import { deleteSparePartRequest, getSparePartRequests, getSparePartRequestFilters } from '@/api/service'
import {
  getTask, addTaskComment
} from '@/api/tasks'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import HtmlColumn from '@/components/ui/datatable/columns/HtmlColumn.vue'

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-feature-input': FeatureInput,
    'mia-feature-textarea': FeatureTextarea,
    'mia-feature-select': FeatureSelect,
    'mia-submit-button': SubmitButton
  },

  data() {
    return {
      mechanics: [],
      sparePartRequests: [],
      filters: {
        mechanic: '',
        orderId: ''
      },
      selectedTask: null,
      showCommentModal: false,
      columns: [
        {
          field: 'item_title',
          label: 'Titel',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/auftragserweiterung/:id'
          }
        },
        {
          field: 'item_context.orderId',
          label: 'Auftragsnummer',
          headerAlign: 'left',
          representedAs: row => row.item_context?.orderId
        },
        {
          field: 'item_created_at',
          label: 'Angefragt am',
          headerAlign: 'left',
          representedAs: (row) => {
            const formattedDateTime = this.formatDateTime(row.item_created_at)

            if (row.item_context.requestedByName) {
              return `${formattedDateTime} von ${row.item_context.requestedByName}`
            }

            return formattedDateTime
          }
        },
        {
          field: 'item_assignee_name',
          label: 'Bearbeiter',
          headerAlign: 'left'
        },
        {
          field: 'item_status_id',
          label: 'Status',
          headerAlign: 'left',
          component: HtmlColumn,
          meta: {
            render(row) {
              const colorClass = {
                pending: 'text-yellow-600',
                in_stock: 'text-green-600 font-bold',
                ordered: 'text-green-600',
                declined: 'text-red-600'
              }[row.item_status_id] || ''

              return `<span class="${colorClass}">${row.item_status}</span>`
            }
          }
        },
        {
          field: 'item_status_comment',
          label: 'Status-Kommentar',
          headerAlign: 'left'
        },
        {
          field: 'item_comment_count',
          label: 'Kommentare',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                label: row => row.item_comment_count,
                onClick: row => this.openCommentModal(row.id)
              }
            ]
          }
        },
        {
          field: '',
          label: 'Aktion',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            actions: row => (['in_stock', 'declined'].includes(row.item_status_id) ? [] : [
              {
                icon: 'pencil-write',
                onClick: r => this.$router.push(`/ersatzteilanfrage/${r.id}`)
              },
              {
                icon: 'bin-1',
                class: 'border-red-500 text-red-500 hover:bg-red-500',
                visible: row => row.item_completed !== '1' && (parseInt(row?.item_context?.requestedBy) === parseInt(this.authUser.id) || row?.item_context?.requestedBy === undefined),
                onClick: r => this.onDelete(r)
              }
            ])
          }
        }
      ],
      activeSparePartRequest: null,
      deletingSparePartRequest: null
    }
  },

  computed: {
    sortedSparePartRequests() {
      const inStockSparePartRequests = this.sparePartRequests.filter(sparePartRequest => sparePartRequest.item_status_id === 'in_stock')
      const orderedSparePartRequests = this.sparePartRequests.filter(sparePartRequest => sparePartRequest.item_status_id === 'ordered')
      const pendingSparePartRequests = this.sparePartRequests.filter(sparePartRequest => sparePartRequest.item_status_id === 'pending')
      const declinedSparePartRequests = this.sparePartRequests.filter(sparePartRequest => sparePartRequest.item_status_id === 'declined')

      return [
        ...inStockSparePartRequests,
        ...orderedSparePartRequests,
        ...pendingSparePartRequests,
        ...declinedSparePartRequests
      ]
    }
  },

  created() {
    this.loadFilters()
    this.loadSparePartRequests()
  },

  mounted() {
    this.$nextTick(() => {
      eventBus.$emit('mia-set-breadcrumb', [
        {
          link: {
            path: '/ersatzteilanfrage'
          },
          text: 'Ersatzteilanfragen'
        }
      ])
    })
  },

  methods: {
    async openCommentModal(id) {
      this.$wait.start('loading task')
      const [error, response] = await getTask(id)
      this.$wait.end('loading task')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kommentare konnten nicht geladen werden']
        })
        return
      }

      this.activeSparePartRequest = id
      this.selectedTask = response
      this.showCommentModal = true
    },

    closeCommentModal() {
      this.activeSparePartRequest = null
      this.selectedTask = null
      this.showCommentModal = false
    },

    async onSaveComment() {
      this.$wait.start('saving comment')
      const [error] = await addTaskComment({
        item_task_id: this.selectedTask.id,
        item_comment: this.selectedTask.new_comment
      })
      this.$wait.end('saving comment')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kommentar konnte nicht gespeichert werden']
        })
        return
      }

      this.sparePartRequests = this.sparePartRequests.map((sparePartRequest) => {
        if (sparePartRequest.id === this.activeSparePartRequest) {
          return {
            ...sparePartRequest,
            item_comment_count: parseInt(sparePartRequest.item_comment_count, 10) + 1
          }
        }

        return sparePartRequest
      })
      this.closeCommentModal()
      eventBus.$emit('task-changed')
      window.eventBus.$emit('mia-info', {
        info: ['Kommentar erfolgreich gespeichert']
      })
    },

    resetFilters() {
      this.$set(this.filters, 'mechanic', '')
      this.$set(this.filters, 'orderId', '')

      this.loadSparePartRequests()
    },

    async loadFilters() {
      this.$wait.start('loading filters')
      const [error, response] = await getSparePartRequestFilters()
      this.$wait.end('loading filters')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Filter konnten nicht geladen werden']
        })
        return
      }

      this.mechanics = response.employees
    },

    async loadSparePartRequests() {
      this.$wait.start('loading spare part requests')
      const [error, response] = await getSparePartRequests({
        mechanic: this.filters.mechanic || undefined,
        order_id: this.filters.orderId || undefined
      })
      this.$wait.end('loading spare part requests')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Ersatzteilanfragen konnten nicht geladen werden']
        })
        return
      }

      this.sparePartRequests = response
    },

    formatDateTime(dateStr) {
      return `${DateTime.fromSQL(dateStr).toFormat('dd.MM.yyyy, HH:mm')} Uhr`
    },

    onDelete(sparePartRequest) {
      this.deletingSparePartRequest = sparePartRequest
    },

    async onDeleteSparePartRequestConfirmed() {
      this.$wait.start('deleting spare part request')
      const [error] = await deleteSparePartRequest(this.deletingSparePartRequest.id)
      this.$wait.end('deleting spare part request')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Der Eintrag konnte nicht gelöscht werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Der Eintrag wurde erfolgreich gelöscht']
      })
      this.deletingSparePartRequest = null
      this.loadSparePartRequests()
    }
  }
}
</script>
